/**
 * Well-known AI services, so choosing one fills the format and address. Any other service
 * speaking one of the two formats works too ("Custom").
 */
import type { AiFormat } from "@protocol/AiFormat";

/** One AI service the settings form offers. */
export interface AiProvider {
  /** Stable id (for the `<select>` and checks). */
  id: string;
  /** Name shown in the list. */
  name: string;
  /** How to talk to it. */
  format: AiFormat;
  /** Its address (empty for "Custom"). */
  baseUrl: string;
  /** A model to start from (the user can type another). */
  model: string;
  /** Whether it needs an API key (local servers do not). */
  needsKey: boolean;
  /** A short hint shown under the form. */
  hint: string;
}

/** Any other service: the user fills everything. */
export const CUSTOM: AiProvider = { id: "custom", name: "Custom", format: "openai", baseUrl: "", model: "", needsKey: false, hint: "Any service speaking the OpenAI or Anthropic format." };

/** The services offered, in the list's order ("Custom" last). */
export const AI_PROVIDERS: AiProvider[] = [
  { id: "openai", name: "OpenAI", format: "openai", baseUrl: "https://api.openai.com/v1", model: "gpt-4.1", needsKey: true, hint: "Key from platform.openai.com." },
  { id: "anthropic", name: "Anthropic (Claude)", format: "anthropic", baseUrl: "https://api.anthropic.com", model: "claude-sonnet-5-5", needsKey: true, hint: "Key from console.anthropic.com." },
  { id: "mistral", name: "Mistral", format: "openai", baseUrl: "https://api.mistral.ai/v1", model: "mistral-large-latest", needsKey: true, hint: "Key from console.mistral.ai." },
  { id: "groq", name: "Groq", format: "openai", baseUrl: "https://api.groq.com/openai/v1", model: "llama-3.3-70b-versatile", needsKey: true, hint: "Key from console.groq.com." },
  { id: "openrouter", name: "OpenRouter", format: "openai", baseUrl: "https://openrouter.ai/api/v1", model: "anthropic/claude-sonnet-5.5", needsKey: true, hint: "One key for many models, from openrouter.ai." },
  { id: "ollama", name: "Ollama (local)", format: "openai", baseUrl: "http://localhost:11434/v1", model: "llama3.1", needsKey: false, hint: "Runs on your machine; no key. In the browser build start it with OLLAMA_ORIGINS=*." },
  { id: "lmstudio", name: "LM Studio (local)", format: "openai", baseUrl: "http://localhost:1234/v1", model: "local-model", needsKey: false, hint: "Start LM Studio's server; no key." },
  CUSTOM,
];

/** The provider whose format and address match these settings, or "Custom". */
export function providerFor(format: AiFormat, baseUrl: string): AiProvider {
  const url = baseUrl.replace(/\/+$/, "");
  return AI_PROVIDERS.find((p) => p.baseUrl && p.format === format && p.baseUrl === url) ?? CUSTOM;
}
